import { useCallback, useEffect, useId, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { createCustomerSchema, createVehicleSchema, classifyServiceKind, getAddonLineMarker, isMinimumAdjustmentLine, priceAddonLine, applyCategoryMinimums } from '@wrapforce/shared';
import type { QuoteLineItemInput, CreateCustomerInput, CreateVehicleInput, QuoteDetail, CatalogResponse, ServiceTypeWithCatalog, LineCoverageSnapshot, AddonService } from '@wrapforce/shared';
import { toast } from 'sonner';
import { Pencil, Plus, Trash2, X, ZoomIn } from 'lucide-react';
import { PLAN_DEFINITIONS } from '@wrapforce/shared/constants';
import { useActiveMembership } from '@/features/session/useActiveMembership';
import { useCustomers, useCustomer, useCreateCustomer } from '@/features/customers/api';
import { useCustomerVehicles, useVehicle, useCreateVehicle } from '@/features/customers/vehiclesApi';
import { useLeads } from '@/features/leads/api';
import { useLocations } from '@/features/locations/api';
import { useLocationStore, ALL_LOCATIONS } from '@/stores/location';
import { useStripeStatus } from '@/features/integrations/api';
import { useUploadMedia } from '@/features/media/api';
import { useAttachExistingMockup, useGenerateMockup, useMockups, useSetMockupAttached, useVehicleMockupHistory } from '@/features/mockups/api';
import { workspacePath } from '@wrapforce/shared/api-client';
import { api } from '@/lib/api';
import { apiErrorMessage } from '@/lib/api-errors';
import { useDebounce } from '@/lib/useDebounce';
import { useLocaleFormatters } from '@/lib/format';
import { useCatalog, useQuote, useSaveQuote, useSendQuote } from './api';
import { SendQuoteCard } from './SendQuoteCard';
import { reconcileAddonLines } from './addon-reconcile';
import { useUpdatePricingRules } from '@/features/settings/api';
import { useAddonServices, useServiceCategoryMinimums } from '@/features/settings/pricing-v2/api';
import { Button } from '@/components/ui/button';
import { Input, Label, FieldError } from '@/components/ui/input';
import { AutocompleteInput } from '@/components/ui/autocomplete-input';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { VehicleAwareWrapLineBuilder, type WrapLineOutput } from './pricing/VehicleAwareWrapLineBuilder';
import { PpfLineBuilder, PPF_ZONE_LABEL_KEYS, PPF_FILM_TIER_LABEL_KEYS, type PpfLineOutput } from './pricing/PpfLineBuilder';
import { TintLineBuilder, TINT_ZONE_LABEL_KEYS, TINT_FILM_GRADE_LABEL_KEYS, type TintLineOutput } from './pricing/TintLineBuilder';
import { CeramicLineBuilder, CERAMIC_PACKAGE_LABEL_KEYS, type CeramicLineOutput } from './pricing/CeramicLineBuilder';
import { PrintLineBuilder, PRINT_COVERAGE_LABEL_KEYS, type PrintLineOutput } from './pricing/PrintLineBuilder';
import { SIZE_CLASS_LABEL_KEYS, useVehicleCatalogSuggestions } from './pricing/VehiclePicker';

type TFn = (key: string, options?: Record<string, unknown>) => string;

export interface DraftLine extends QuoteLineItemInput {
  key: string;
  // §29 vehicle-aware lines only: the pure engine's own computed total, kept
  // alongside the line so the autosave payload can submit it verbatim instead
  // of the old formula's estimate (see the autosave effect below).
  lineTotalCents?: number;
  // Display-only (never sent to the server — the autosave payload below lists
  // its fields explicitly), so the unified line list can show which vehicle a
  // wrap line is for without re-deriving it from vehicleCatalogTrimId/workspaceVehicleId.
  vehicleLabel?: string;
  // Display-only, like vehicleLabel above - persisted via the payload's
  // serviceName and restored from li.serviceName on hydration, so an add-on
  // line's own name shows up without a serviceKind to key off of.
  addonName?: string;
}

// The in-progress (not-yet-added) Step-3 service-builder state: which kind's
// builder is open, plus that builder's own opaque serialized state (all the
// fields the shop had entered). Persisted on the draft under
// pricing_snapshot.draftServiceState and restored open on reopen - never run
// through the server line recompute (unlike a committed line). Each builder
// owns the shape of `state`.
export interface DraftServiceState {
  kind: 'wrap' | 'ppf' | 'tint' | 'ceramic' | 'print';
  state: Record<string, unknown>;
}

// Bridges a §29 vehicle-aware wrap line (VehicleAwareWrapLineBuilder's output)
// into the existing DraftLine/QuoteLineItemInput shape the rest of this page's
// save/estimate/autosave flow already understands. The old coverage/color
// columns are vestigial for a §29 line — populated here with harmless,
// non-crashing values rather than left to crash the old flow.
function buildDraftLineFromWrapOutput(wrapLine: WrapLineOutput, service: ServiceTypeWithCatalog, sort: number): DraftLine {
  return {
    key: crypto.randomUUID(),
    serviceTypeId: service.id,
    coverageId: service.coverages[0]?.id ?? '',
    materialId: wrapLine.materialId,
    color: '',
    finish: wrapLine.finishKey,
    notes: '',
    sort,
    serviceKind: wrapLine.serviceKind,
    vehicleCatalogTrimId: wrapLine.vehicleCatalogTrimId,
    workspaceVehicleId: wrapLine.workspaceVehicleId,
    coverage: wrapLine.coverage,
    disassemblyLevel: wrapLine.disassemblyLevel,
    doorJambs: wrapLine.doorJambs,
    estimatedMinutes: wrapLine.estimatedMinutes,
    filmFtX10: wrapLine.filmFtX10,
    materialCostCents: wrapLine.materialCostCents,
    laborCostCents: wrapLine.laborCostCents,
    breakdown: wrapLine.breakdown,
    profileVersionId: wrapLine.profileVersionId,
    lineTotalCents: wrapLine.lineTotalCents,
    vehicleLabel: wrapLine.vehicleLabel,
    materialInventoryItemId: wrapLine.materialInventoryItemId,
  };
}

// PPF/Tint/Ceramic placeholder legacy-table IDs: these builders never touch
// coverage_options/materials themselves (they price entirely off the new
// engine tables), so the placeholder IDs are harmless carry-through values
// for the legacy columns quoteLineItemInputSchema still requires - same
// rationale as buildDraftLineFromWrapOutput's materialId/coverageId above.
interface PlaceholderLegacyIds {
  serviceTypeId: string;
  coverageId: string;
  materialId: string;
}

function buildDraftLineFromPpfOutput(output: PpfLineOutput, placeholderIds: PlaceholderLegacyIds, sort: number): DraftLine {
  // A new line (from the current inventory-backed PpfLineBuilder) sets
  // neither ppfProductId nor filmTier - the film comes from Inventory via
  // materialInventoryItemId instead, and the server recomputes the total off
  // the zone def + inventory item price (see resolve.ts's new inventory
  // branch). Both legacy shapes are kept for old saved lines being re-edited.
  const coverage: LineCoverageSnapshot = output.ppfProductId
    ? {
        serviceKind: 'ppf',
        zoneKey: output.zoneKey,
        sizeClass: output.sizeClass,
        ppfProductId: output.ppfProductId,
        designMinutes: 0,
        fixedAddOnCents: 0,
      }
    : output.filmTier
      ? {
          serviceKind: 'ppf',
          zoneKey: output.zoneKey,
          sizeClass: output.sizeClass,
          filmTier: output.filmTier,
          designMinutes: 0,
          fixedAddOnCents: 0,
        }
      : {
          serviceKind: 'ppf',
          zoneKey: output.zoneKey,
          sizeClass: output.sizeClass,
          designMinutes: 0,
          fixedAddOnCents: 0,
        };
  return {
    key: crypto.randomUUID(),
    serviceTypeId: placeholderIds.serviceTypeId,
    coverageId: placeholderIds.coverageId,
    materialId: placeholderIds.materialId,
    color: '',
    finish: '',
    notes: '',
    sort,
    serviceKind: output.serviceKind,
    coverage,
    estimatedMinutes: output.estimatedMinutes,
    lineTotalCents: output.lineTotalCents,
    breakdown: output.breakdown,
    profileVersionId: output.profileVersionId,
    vehicleLabel: output.vehicleLabel,
    vehicleCatalogTrimId: output.vehicleCatalogTrimId,
    workspaceVehicleId: output.workspaceVehicleId,
    materialInventoryItemId: output.materialInventoryItemId ?? null,
    materialQty: output.materialQty ?? null,
    materialUnit: output.materialUnit ?? null,
    reservedMaterialCostCents: output.reservedMaterialCostCents ?? null,
  };
}

function buildDraftLineFromTintOutput(output: TintLineOutput, placeholderIds: PlaceholderLegacyIds, sort: number): DraftLine {
  // A new line (from the current inventory-backed TintLineBuilder) sets
  // neither tintProductId nor filmGrade - the film comes from Inventory via
  // materialInventoryItemId instead, and the server recomputes the total off
  // the zone def + inventory item price (see resolve.ts's new inventory
  // branch). Both legacy shapes are kept for old saved lines being re-edited.
  const coverage: LineCoverageSnapshot = output.tintProductId
    ? {
        serviceKind: 'tint',
        zoneKey: output.zoneKey,
        sizeClass: output.sizeClass,
        tintProductId: output.tintProductId,
        vlt: output.vlt,
        designMinutes: 0,
        fixedAddOnCents: 0,
      }
    : output.filmGrade
      ? {
          serviceKind: 'tint',
          zoneKey: output.zoneKey,
          sizeClass: output.sizeClass,
          filmGrade: output.filmGrade,
          vlt: output.vlt,
          designMinutes: 0,
          fixedAddOnCents: 0,
        }
      : {
          serviceKind: 'tint',
          zoneKey: output.zoneKey,
          sizeClass: output.sizeClass,
          vlt: output.vlt,
          designMinutes: 0,
          fixedAddOnCents: 0,
        };
  return {
    key: crypto.randomUUID(),
    serviceTypeId: placeholderIds.serviceTypeId,
    coverageId: placeholderIds.coverageId,
    materialId: placeholderIds.materialId,
    color: '',
    finish: '',
    notes: '',
    sort,
    serviceKind: output.serviceKind,
    coverage,
    estimatedMinutes: output.estimatedMinutes,
    lineTotalCents: output.lineTotalCents,
    breakdown: output.breakdown,
    profileVersionId: output.profileVersionId,
    vehicleLabel: output.vehicleLabel,
    vehicleCatalogTrimId: output.vehicleCatalogTrimId,
    workspaceVehicleId: output.workspaceVehicleId,
    materialInventoryItemId: output.materialInventoryItemId ?? null,
    materialQty: output.materialQty ?? null,
    materialUnit: output.materialUnit ?? null,
    reservedMaterialCostCents: output.reservedMaterialCostCents ?? null,
  };
}

function buildDraftLineFromCeramicOutput(output: CeramicLineOutput, placeholderIds: PlaceholderLegacyIds, sort: number): DraftLine {
  // New ceramic lines are material-only: the snapshot carries neither a
  // coatingProductId (legacy product catalog) nor a packageKey (legacy tier).
  const coverage: LineCoverageSnapshot = {
    serviceKind: 'ceramic',
    sizeClass: output.sizeClass,
    designMinutes: 0,
    fixedAddOnCents: 0,
  };
  return {
    key: crypto.randomUUID(),
    serviceTypeId: placeholderIds.serviceTypeId,
    coverageId: placeholderIds.coverageId,
    materialId: placeholderIds.materialId,
    color: '',
    finish: '',
    notes: '',
    sort,
    serviceKind: output.serviceKind,
    coverage,
    estimatedMinutes: output.estimatedMinutes,
    lineTotalCents: output.lineTotalCents,
    breakdown: output.breakdown,
    profileVersionId: output.profileVersionId,
    vehicleLabel: output.vehicleLabel,
    vehicleCatalogTrimId: output.vehicleCatalogTrimId,
    workspaceVehicleId: output.workspaceVehicleId,
    materialInventoryItemId: output.materialInventoryItemId ?? null,
    materialQty: output.materialQty ?? null,
    materialUnit: output.materialUnit ?? null,
    reservedMaterialCostCents: output.reservedMaterialCostCents ?? null,
  };
}

// New printed-wrap lines mirror the ceramic/PPF/tint shape: material-only from
// Inventory (print media + laminate) plus a design/print engine result priced
// off the wrap profile version (pricePrintLine reuses the same rates and
// vehicle base ft/minutes as the wrap engine - see PrintLineBuilder.tsx).
function buildDraftLineFromPrintOutput(output: PrintLineOutput, placeholderIds: PlaceholderLegacyIds, sort: number): DraftLine {
  const coverage: LineCoverageSnapshot = {
    serviceKind: 'print',
    designId: output.designId,
    designName: output.designName,
    coverageLevel: output.coverageLevel,
    spotFtX10: output.spotFtX10,
    spotMinutes: output.spotMinutes,
    laminateInventoryItemId: output.laminateInventoryItemId,
    printCostCentsPerSqft: output.printCostCentsPerSqft,
    designMode: output.designMode,
    designFlatCents: output.designFlatCents,
    designMinutes: output.designMinutes,
    revisionRounds: output.revisionRounds,
    removalTier: output.removalTier,
    fixedAddOnCents: output.fixedAddOnCents,
  };
  return {
    key: crypto.randomUUID(),
    serviceTypeId: placeholderIds.serviceTypeId,
    coverageId: placeholderIds.coverageId,
    materialId: placeholderIds.materialId,
    color: '',
    finish: '',
    notes: '',
    sort,
    serviceKind: output.serviceKind,
    coverage,
    estimatedMinutes: output.estimatedMinutes,
    lineTotalCents: output.lineTotalCents,
    breakdown: output.breakdown,
    profileVersionId: output.profileVersionId,
    vehicleLabel: output.vehicleLabel,
    vehicleCatalogTrimId: output.vehicleCatalogTrimId,
    workspaceVehicleId: output.workspaceVehicleId,
    materialInventoryItemId: output.materialInventoryItemId ?? null,
    materialQty: output.materialQty ?? null,
    materialUnit: output.materialUnit ?? null,
    reservedMaterialCostCents: output.reservedMaterialCostCents ?? null,
  };
}

export function NewQuotePage() {
  const { t } = useTranslation('quotes');
  const { membership } = useActiveMembership();
  const workspaceId = membership?.workspaceId ?? null;
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [customerId, setCustomerId] = useState<string | null>(params.get('customerId'));
  const [vehicleId, setVehicleId] = useState<string | null>(params.get('vehicleId'));
  const [selectedPhotoId, setSelectedPhotoId] = useState<string | null>(null);
  const [lines, setLines] = useState<DraftLine[]>([]);
  // In-progress Step-3 builder state (not yet added as a line) - persisted on
  // the draft so leaving/reopening restores the open builder exactly as left.
  const [draftServiceState, setDraftServiceState] = useState<DraftServiceState | null>(null);
  const [depositPct, setDepositPct] = useState(0);
  const [discountName, setDiscountName] = useState('');
  const [discountPct, setDiscountPct] = useState(0);
  const [taxEnabled, setTaxEnabled] = useState(true);
  const [depositCustom, setDepositCustom] = useState(false);
  const [depositCustomCents, setDepositCustomCents] = useState(0);
  const [customerNotes, setCustomerNotes] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  // How far out the customer's preferred booking date must be on this quote
  // (days; 0 = no restriction). Per-quote on purpose - small shops with film
  // on hand want a shorter lead on some quotes only.
  const [bookingLeadDays, setBookingLeadDays] = useState(14);
  const [savedQuoteId, setSavedQuoteId] = useState<string | null>(params.get('quoteId'));
  const [locationId, setLocationId] = useState<string | undefined>(undefined);

  const { data: stripeStatus } = useStripeStatus(workspaceId);
  const stripeLinked = !!stripeStatus?.connected && !!stripeStatus?.chargesEnabled;

  const { data: catalog } = useCatalog(workspaceId);
  const saveQuote = useSaveQuote(workspaceId, savedQuoteId);
  const sendQuote = useSendQuote(workspaceId, savedQuoteId ?? '');

  const gateOn = membership ? PLAN_DEFINITIONS[membership.plan].gates.multiLocation : false;
  const { data: locationsData } = useLocations(workspaceId);
  const activeLocations = (locationsData?.items ?? []).filter((l) => l.isActive);
  const showLocationSelect = gateOn && activeLocations.length >= 2;
  const switcherLocationId = useLocationStore((s) => s.activeLocationId);
  const defaultLocationId = switcherLocationId !== ALL_LOCATIONS ? switcherLocationId : activeLocations[0]?.id;

  // When opened with ?quoteId=, load the existing quote's data once catalog is ready so its
  // fields (customer/vehicle/lines/notes/deposit) populate for editing instead of starting blank.
  const editingQuoteIdRef = useRef(params.get('quoteId'));
  const hydratedRef = useRef(false);
  const { data: existingQuote } = useQuote(workspaceId, editingQuoteIdRef.current ?? undefined);

  const locationDefaultedRef = useRef(false);
  useEffect(() => {
    if (locationDefaultedRef.current || !showLocationSelect) return;
    if (editingQuoteIdRef.current) {
      // Opened with ?quoteId= (either editing a draft or arriving from a lead
      // conversion's navigate() call) - wait for the quote to load so its own
      // locationId wins over the switcher default.
      if (!existingQuote) return;
      locationDefaultedRef.current = true;
      setLocationId(existingQuote.locationId);
      return;
    }
    if (defaultLocationId) {
      locationDefaultedRef.current = true;
      setLocationId(defaultLocationId);
    }
  }, [showLocationSelect, existingQuote, defaultLocationId]);

  useEffect(() => {
    if (hydratedRef.current || !existingQuote || !catalog) return;
    hydratedRef.current = true;
    setCustomerId(existingQuote.customerId);
    setVehicleId(existingQuote.vehicleId);
    setCustomerNotes(existingQuote.customerNotes);
    setInternalNotes(existingQuote.internalNotes);
    setDepositPct(existingQuote.depositPct);
    setDiscountName(existingQuote.discountName);
    setDiscountPct(existingQuote.discountPct);
    setTaxEnabled(existingQuote.taxEnabled);
    setBookingLeadDays(existingQuote.bookingMinLeadDays);
    if (existingQuote.draftServiceState && typeof existingQuote.draftServiceState.kind === 'string' && existingQuote.draftServiceState.state) {
      setDraftServiceState(existingQuote.draftServiceState as unknown as DraftServiceState);
    }
    if (existingQuote.depositCents > 0 && existingQuote.depositPct === 0) {
      setDepositCustom(true);
      setDepositCustomCents(existingQuote.depositCents);
    }
    setLines(
      existingQuote.lineItems
        .filter((li) => !isMinimumAdjustmentLine(li.breakdown))
        .map((li) => {
        // §36 add-on service lines (serviceKind unset, marked via
        // breakdown.addon) - restore straight from the marker + persisted
        // fields, same rationale as the engine-kind branch below.
        const addonMarker = getAddonLineMarker(li.breakdown);
        if (addonMarker) {
          return {
            key: crypto.randomUUID(),
            serviceTypeId: li.serviceTypeId ?? '',
            coverageId: '',
            materialId: li.materialId ?? '',
            color: '',
            finish: '',
            notes: li.notes,
            sort: li.sort,
            estimatedMinutes: li.estimatedMinutes ?? null,
            lineTotalCents: li.lineTotalCents,
            breakdown: li.breakdown,
            addonName: li.serviceName,
          };
        }
        // §32 engine-kind lines (serviceKind set) carry their own coverage/
        // estimatedMinutes/lineTotalCents/profileVersionId/breakdown from
        // save_quote - use those directly rather than the legacy coverage/
        // materialId lookup below, which won't resolve for these lines.
        if (li.serviceKind) {
          return {
            key: crypto.randomUUID(),
            serviceTypeId: li.serviceTypeId ?? '',
            coverageId: '',
            materialId: li.materialId ?? '',
            color: li.color,
            finish: li.finish,
            notes: li.notes,
            sort: li.sort,
            serviceKind: li.serviceKind,
            vehicleCatalogTrimId: li.vehicleCatalogTrimId,
            workspaceVehicleId: li.workspaceVehicleId,
            coverage: li.coverage,
            disassemblyLevel: li.disassemblyLevel,
            doorJambs: li.doorJambs,
            // Heal lines saved while save_quote dropped estimated_minutes
            // (0059-0068 regression): recover the estimate from laborHours so
            // the next autosave re-persists it instead of zeroing labor_hours.
            estimatedMinutes: li.estimatedMinutes ?? (li.laborHours > 0 ? Math.round(li.laborHours * 60) : null),
            filmFtX10: li.filmFtX10,
            materialCostCents: li.materialCostCents,
            laborCostCents: li.laborCostCents,
            breakdown: li.breakdown,
            profileVersionId: li.profileVersionId,
            lineTotalCents: li.lineTotalCents,
            materialInventoryItemId: li.materialInventoryItemId ?? null,
            materialQty: li.materialQty ?? null,
            materialUnit: li.materialUnit ?? null,
            reservedMaterialCostCents: li.reservedMaterialCostCents ?? null,
          };
        }
        const service = catalog.serviceTypes.find((s) => s.id === li.serviceTypeId);
        const coverage = service?.coverages.find((c) => c.name === li.coverageName);
        return {
          key: crypto.randomUUID(),
          serviceTypeId: li.serviceTypeId ?? service?.id ?? '',
          coverageId: coverage?.id ?? service?.coverages[0]?.id ?? '',
          materialId: li.materialId ?? service?.materials[0]?.id ?? '',
          color: li.color,
          finish: li.finish,
          notes: li.notes,
          sort: li.sort,
          sizeClass: li.sizeClass ?? 'sedan',
          // Same heal as the engine branch above: stripped lines (serviceKind
          // lost to the 0059-0068 save_quote regression) land here, and losing
          // the estimate again on autosave would zero labor_hours for good.
          estimatedMinutes: li.estimatedMinutes ?? (li.laborHours > 0 ? Math.round(li.laborHours * 60) : null),
          lineTotalCents: li.lineTotalCents,
        };
      }),
    );
  }, [existingQuote, catalog]);

  // Best-effort service preselect from the lead's requirement text (leads
  // carry no structured service field, only free text).
  const inferredKind = useMemo(() => inferServiceKind(internalNotes), [internalNotes]);

  // §36 add-on services + per-category minimums (Settings > Pricing). Fetched
  // here (not inside ServiceDetailsCard) so both the add-on picker and the
  // minimum-adjustment math below share one request each.
  const { data: addonServicesData } = useAddonServices(workspaceId);

  // Add-on lines carry a rate snapshot in breakdown.addon; the server reprices
  // them from the live addon_services row and rejects a stale total. Keep the
  // lines in step with the catalog whenever it (re)loads, including hydrating
  // an older quote, so the save can never trip that guard for a rate change.
  useEffect(() => {
    if (!addonServicesData) return;
    const result = reconcileAddonLines(lines, addonServicesData);
    if (!result.changed) return;
    setLines(result.lines);
    toast.info(t('newQuote.addonPricesUpdated'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addonServicesData, lines]);
  const { data: categoryMinimumsData } = useServiceCategoryMinimums(workspaceId);

  const lineSumCents = lines.reduce((sum, l) => sum + (l.lineTotalCents ?? 0), 0);

  // Locally mirrors the server's authoritative minimum-adjustment math byte
  // for byte (see save_quote) so the estimate displays the inflated subtotal
  // before saving - these adjustments are display-only and never sent in the
  // autosave payload; the server strips and rebuilds the real lines itself.
  const categoryAdjustments = useMemo(() => {
    const minimums = categoryMinimumsData ?? [];
    const inputs = (['wrap', 'ppf', 'tint', 'ceramic', 'print'] as const)
      .map((kind) => {
        const row = minimums.find((m) => m.serviceCategory === kind);
        if (!row || !row.enabled || row.minCents <= 0) return null;
        // Mirrors the server: a category minimum only floors quotes that
        // actually contain a line of that category.
        const categoryLines = lines.filter((l) => l.serviceKind === kind);
        if (categoryLines.length === 0) return null;
        const subtotalCents = categoryLines.reduce((sum, l) => sum + (l.lineTotalCents ?? 0), 0);
        return { category: kind, subtotalCents, minCents: row.minCents, waived: false };
      })
      .filter((input): input is NonNullable<typeof input> => input !== null);
    return applyCategoryMinimums(inputs);
  }, [categoryMinimumsData, lines]);
  const categoryAdjustmentCents = categoryAdjustments.reduce((sum, a) => sum + a.adjustmentCents, 0);

  const defaultShortfallCents = useMemo(() => {
    const defaultRow = (categoryMinimumsData ?? []).find((m) => m.serviceCategory === 'default');
    if (!defaultRow || !defaultRow.enabled || defaultRow.minCents <= 0 || lines.length === 0) return 0;
    const shortfall = defaultRow.minCents - (lineSumCents + categoryAdjustmentCents);
    return shortfall > 0 ? shortfall : 0;
  }, [categoryMinimumsData, lines.length, lineSumCents, categoryAdjustmentCents]);

  const subtotalCents = lineSumCents + categoryAdjustmentCents + defaultShortfallCents;
  const taxRateBps = catalog?.pricingRules.taxRateBps ?? 0;
  const discountCents = Math.round((subtotalCents * discountPct) / 100);
  const taxableCents = subtotalCents - discountCents;
  const taxCents = taxEnabled ? Math.round((taxableCents * taxRateBps) / 10000) : 0;
  const totalCents = taxableCents + taxCents;
  const depositCents = depositCustom
    ? Math.min(depositCustomCents, totalCents)
    : Math.ceil((totalCents * depositPct) / 100 / 100) * 100;
  const allLineDetailsComplete = lines.length > 0;
  // A printed wrap design's own renders are shown to the customer on the
  // quote link instead, so AI mockups are redundant (and would show the
  // wrong artwork) once one is chosen.
  const printDesignChosen = lines.some((l) => l.coverage?.serviceKind === 'print' && !!l.coverage.designId);

  // A deposit can only be charged once Stripe is fully linked (connected + charges enabled).
  // Otherwise force "No deposit" so the quote can't request a payment it can't collect.
  useEffect(() => {
    if (!stripeLinked && depositPct !== 0) setDepositPct(0);
    if (!stripeLinked && depositCustom) {
      setDepositCustom(false);
      setDepositCustomCents(0);
    }
  }, [stripeLinked, depositPct, depositCustom]);

  // Autosave a draft as soon as there's enough to save, so pricing/mockup/send unlock without an explicit "next" click.
  // Debounced (at most one call per pause in editing, deduped against the
  // last saved payload - never a call per keystroke) and flushed once on
  // unmount, so navigating away (e.g. to a vehicle's panel-pricing settings)
  // never loses the draft and adds no steady-state server load.
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedKeyRef = useRef<string | null>(null);
  const pendingSaveRef = useRef<{ key: string; payload: unknown } | null>(null);
  const saveQuoteRef = useRef(saveQuote);
  saveQuoteRef.current = saveQuote;
  useEffect(() => {
    // Lines are NOT required - a customer+vehicle selection alone is worth a
    // draft, so leaving the page (or pressing back) never loses that work.
    // A quote converted from a lead already exists without a vehicle (0074),
    // so edits to it must keep saving before one is picked.
    if (!customerId || (!vehicleId && !savedQuoteId)) return;
    const payload = {
      customerId,
      vehicleId,
      lineItems: lines.map((l, i) => ({
        serviceTypeId: l.serviceTypeId || null, materialId: l.materialId || null,
        serviceName: getAddonLineMarker(l.breakdown) ? (l.addonName ?? 'Add-on service') : SERVICE_KIND_LABELS[l.serviceKind ?? 'wrap'], coverageName: '', materialBrand: '', materialSeries: '',
        color: l.color, finish: l.finish,
        sqft: null, laborHours: (l.estimatedMinutes ?? 0) / 60,
        materialRateCents: 0, laborRateCents: 0, brandMultiplier: 1, sizeClass: null,
        lineTotalCents: l.lineTotalCents ?? 0, notes: l.notes, sort: i,
        serviceKind: l.serviceKind, vehicleCatalogTrimId: l.vehicleCatalogTrimId, workspaceVehicleId: l.workspaceVehicleId,
        coverage: l.coverage, disassemblyLevel: l.disassemblyLevel, doorJambs: l.doorJambs,
        estimatedMinutes: l.estimatedMinutes, filmFtX10: l.filmFtX10, materialCostCents: l.materialCostCents,
        laborCostCents: l.laborCostCents, breakdown: l.breakdown, profileVersionId: l.profileVersionId,
        materialInventoryItemId: l.materialInventoryItemId, materialQty: l.materialQty,
        materialUnit: l.materialUnit, reservedMaterialCostCents: l.reservedMaterialCostCents,
      })),
      customerNotes,
      internalNotes,
      depositPct,
      discountName,
      discountPct,
      taxEnabled,
      depositCustomCents: depositCustom ? depositCustomCents : null,
      bookingMinLeadDays: bookingLeadDays,
      priceSource: 'ai' as const,
      pricingSnapshot: { lines: lines.map((l) => ({ serviceKind: l.serviceKind, lineTotalCents: l.lineTotalCents, breakdown: l.breakdown })), draftServiceState } as unknown as Record<string, unknown>,
      locationId: showLocationSelect ? locationId : undefined,
    };
    const key = JSON.stringify(payload);
    if (key === lastSavedKeyRef.current) return;
    pendingSaveRef.current = { key, payload };
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      lastSavedKeyRef.current = key;
      pendingSaveRef.current = null;
      saveQuote
        .mutateAsync(payload)
        .then((result) => setSavedQuoteId(result.id))
        .catch((err: unknown) => {
          toast.error(t('newQuote.autosaveFailed'), { description: apiErrorMessage(err) });
          // A rejected total usually means the add-on catalog is stale; a
          // refetch feeds the reconcile effect above, which re-triggers autosave.
          void queryClient.invalidateQueries({ queryKey: [workspaceId, 'addon-services'] });
        });
    }, 3_000);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, vehicleId, savedQuoteId, lines, draftServiceState, customerNotes, internalNotes, depositPct, discountName, discountPct, taxEnabled, depositCustom, depositCustomCents, showLocationSelect, locationId, bookingLeadDays]);

  // Once the draft exists on the server, reflect its id in the URL (replace,
  // no new history entry) so browser back / refresh re-opens this same draft
  // via the ?quoteId= hydration path above instead of a blank new quote.
  useEffect(() => {
    if (savedQuoteId && params.get('quoteId') !== savedQuoteId) {
      const next = new URLSearchParams(params);
      next.set('quoteId', savedQuoteId);
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedQuoteId]);

  // Unmount-only flush: if a debounced save was still pending when the page
  // is left, fire it immediately (fire-and-forget) so the draft isn't lost.
  useEffect(() => () => {
    const pending = pendingSaveRef.current;
    if (pending && pending.key !== lastSavedKeyRef.current) {
      lastSavedKeyRef.current = pending.key;
      pendingSaveRef.current = null;
      saveQuoteRef.current.mutateAsync(pending.payload as Parameters<typeof saveQuote.mutateAsync>[0]).catch((err: unknown) => {
        toast.error(t('newQuote.autosaveFailed'), { description: apiErrorMessage(err) });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Await-able flush for actions that need the draft persisted server-side
  // before they run (e.g. generating a mockup reads quote_line_items). Without
  // this, clicking Generate within the 3s autosave debounce hits a quote whose
  // line items aren't saved yet and fails until the debounce lands.
  const flushSave = useCallback(async () => {
    const pending = pendingSaveRef.current;
    if (!pending || pending.key === lastSavedKeyRef.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    lastSavedKeyRef.current = pending.key;
    pendingSaveRef.current = null;
    const result = await saveQuoteRef.current.mutateAsync(
      pending.payload as Parameters<typeof saveQuote.mutateAsync>[0],
    );
    setSavedQuoteId(result.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-4.5 p-6 xl:grid-cols-[1fr_350px] xl:items-start">
      {/* left column */}
      <div className="flex min-w-0 flex-col gap-4">
        <div className="flex items-center gap-2.5">
          <span className="font-mono text-xs font-semibold text-muted">{savedQuoteId ? `Q-${savedQuoteId.slice(0, 4).toUpperCase()}` : t('newQuote.headerNew')}</span>
          <span className="bg-surface-2 px-2 py-0.75 text-[10px] font-bold uppercase tracking-[.04em] text-muted">{t('newQuote.draftBadge')}</span>
          {saveQuote.isPending ? <span className="ml-auto text-xs font-semibold text-muted">{t('newQuote.saving')}</span> : null}
        </div>

        {showLocationSelect ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="quote-location">{t('newQuote.location.label')}</Label>
            <select
              id="quote-location"
              value={locationId ?? ''}
              onChange={(e) => setLocationId(e.target.value)}
              className="h-10 rounded-[var(--radius-input)] border border-border bg-surface px-3 text-sm"
            >
              {activeLocations.map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          </div>
        ) : null}

        <CustomerCard
          workspaceId={workspaceId}
          customerId={customerId}
          onSelectCustomer={(id) => {
            if (id === customerId) return;
            setCustomerId(id);
            setVehicleId(null);
            setSelectedPhotoId(null);
            setLines([]);
          }}
          onLeadConverted={(result) => {
            setCustomerId(result.customerId);
            setVehicleId(result.vehicleId);
            setSavedQuoteId(result.quoteId);
            locationDefaultedRef.current = true;
            setLocationId(result.locationId);
          }}
        />

        <VehicleAndPhotosCard
          key={`vehicle-${customerId ?? 'none'}`}
          workspaceId={workspaceId}
          customerId={customerId}
          vehicleId={vehicleId}
          onSelectVehicle={(id) => {
            setVehicleId(id);
            setSelectedPhotoId(null);
          }}
          selectedPhotoId={selectedPhotoId}
          onSelectPhoto={setSelectedPhotoId}
        />

        {internalNotes ? (
          <div className="border border-border bg-surface-2 p-3">
            <div className="text-xs font-bold uppercase tracking-wide text-muted">{t('newQuote.leadRequest')}</div>
            <p className="mt-1.5 whitespace-pre-line text-sm text-ink">{internalNotes}</p>
          </div>
        ) : null}

        <ServiceDetailsCard
          key={`service-${customerId ?? 'none'}`}
          workspaceId={workspaceId}
          customerId={customerId}
          vehicleId={vehicleId}
          catalog={catalog}
          lines={lines}
          setLines={setLines}
          customerNotes={customerNotes}
          setCustomerNotes={setCustomerNotes}
          defaultKind={inferredKind}
          draftServiceState={draftServiceState}
          setDraftServiceState={setDraftServiceState}
          addonServices={(addonServicesData ?? []).filter((a) => a.active)}
        />

      </div>

      {/* right rail */}
      <div className="flex flex-col gap-4 xl:sticky xl:top-5">
        <EstimateCard
          workspaceId={workspaceId}
          lines={lines}
          catalog={catalog}
          subtotalCents={subtotalCents}
          categoryAdjustments={categoryAdjustments}
          defaultShortfallCents={defaultShortfallCents}
          taxRateBps={taxRateBps}
          taxEnabled={taxEnabled}
          setTaxEnabled={setTaxEnabled}
          taxCents={taxCents}
          discountName={discountName}
          setDiscountName={setDiscountName}
          discountPct={discountPct}
          setDiscountPct={setDiscountPct}
          discountCents={discountCents}
          totalCents={totalCents}
          depositPct={depositPct}
          setDepositPct={setDepositPct}
          depositCents={depositCents}
          depositCustom={depositCustom}
          setDepositCustom={setDepositCustom}
          depositCustomCents={depositCustomCents}
          setDepositCustomCents={setDepositCustomCents}
          stripeLinked={stripeLinked}
        />

        <MockupPanel
          workspaceId={workspaceId}
          quoteId={savedQuoteId}
          vehicleId={vehicleId}
          detailsComplete={allLineDetailsComplete}
          selectedPhotoId={selectedPhotoId}
          onSelectPhoto={setSelectedPhotoId}
          onFlushSave={flushSave}
          printDesignChosen={printDesignChosen}
        />

        <SendQuoteCard
          workspaceId={workspaceId}
          customerId={customerId}
          vehicleId={vehicleId}
          quoteId={lines.length > 0 ? savedQuoteId : null}
          sendQuote={sendQuote}
          totalCents={totalCents}
          depositCents={depositCents}
          bookingLeadDays={bookingLeadDays}
          setBookingLeadDays={setBookingLeadDays}
          onDone={() => navigate(`/quotes/${savedQuoteId}`)}
        />
      </div>
    </div>
  );
}

function CustomerCard({
  workspaceId, customerId, onSelectCustomer, onLeadConverted,
}: {
  workspaceId: string | null; customerId: string | null; onSelectCustomer: (id: string) => void;
  onLeadConverted: (result: { customerId: string; vehicleId: string | null; quoteId: string; locationId: string }) => void;
}) {
  const { t } = useTranslation('quotes');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const searchQuery = debouncedSearch.length >= 2 ? debouncedSearch : undefined;
  const [addOpen, setAddOpen] = useState(false);
  const [convertingLeadId, setConvertingLeadId] = useState<string | null>(null);
  const { data: customers } = useCustomers(searchQuery ? workspaceId : null, { query: searchQuery });
  const { data: selectedCustomer } = useCustomer(workspaceId, customerId ?? undefined);
  const { data: leadsData } = useLeads(searchQuery ? workspaceId : null, { query: searchQuery });
  const leads = (leadsData?.items ?? []).filter((l) => l.status !== 'quoted' && l.status !== 'won');

  async function handlePickLead(lead: { id: string; name: string; email: string | null; phone: string | null }) {
    if (!workspaceId) return;
    setConvertingLeadId(lead.id);
    try {
      const { quoteId } = await api.post<{ quoteId: string }>(workspacePath(workspaceId, `/leads/${lead.id}/convert`), {
        newCustomer: { fullName: lead.name, email: lead.email || undefined, phone: lead.phone || undefined },
      });
      const quote = await api.get<QuoteDetail>(workspacePath(workspaceId, `/quotes/${quoteId}`));
      onLeadConverted({ customerId: quote.customerId, vehicleId: quote.vehicleId, quoteId: quote.id, locationId: quote.locationId });
      toast.success(t('newQuote.toast.leadConverted'));
    } finally {
      setConvertingLeadId(null);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('newQuote.customerCard.title')}</CardTitle>
        <Button size="sm" variant="secondary" className="ml-auto gap-1.5" onClick={() => setAddOpen(true)}>
          <Plus size={14} /> {t('newQuote.customerCard.newCustomer')}
        </Button>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {selectedCustomer && customerId ? (
          <div className="flex items-center gap-2 border border-accent bg-accent/10 px-3 py-2 text-sm font-semibold">
            <span className="bg-accent/15 px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-[.06em] text-accent-ink">
              {t('newQuote.customerCard.selected')}
            </span>
            <span className="flex flex-col">
              <span>{selectedCustomer.fullName}</span>
              <span className="text-xs font-normal text-muted">{[selectedCustomer.phone, selectedCustomer.email].filter(Boolean).join(' · ') || t('newQuote.customerCard.noContactInfo')}</span>
            </span>
          </div>
        ) : null}
        <Input placeholder={t('newQuote.customerCard.searchPlaceholder')} value={search} onChange={(e) => setSearch(e.target.value)} />
        {!searchQuery ? (customerId ? null : <p className="text-sm text-muted">{t('newQuote.customerCard.typeToSearch')}</p>) : (
        <div className="flex flex-col gap-1.5">
          {(customers?.items ?? []).map((c) => (
            <button
              key={c.id}
              onClick={() => onSelectCustomer(c.id)}
              className={`flex items-center gap-2 border px-3 py-2 text-left text-sm ${customerId === c.id ? 'border-accent bg-accent/10 font-semibold' : 'border-border hover:bg-surface-2'}`}
            >
              <span className="bg-surface-2 px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-[.06em] text-muted">
                {c.totalSpentCents > 0 ? t('newQuote.customerCard.returning') : t('newQuote.customerCard.new')}
              </span>
              <span className="flex flex-col">
                <span>{c.fullName}</span>
                <span className="text-xs font-normal text-muted">{[c.phone, c.email].filter(Boolean).join(' · ') || t('newQuote.customerCard.noContactInfo')}</span>
              </span>
            </button>
          ))}
          {leads.map((l) => (
            <button
              key={l.id}
              disabled={convertingLeadId === l.id}
              onClick={() => void handlePickLead(l)}
              className="flex items-center gap-2 border border-dashed border-border px-3 py-2 text-left text-sm hover:bg-surface-2 disabled:opacity-50"
            >
              <span className="bg-accent/15 px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-[.06em] text-accent-ink">
                {convertingLeadId === l.id ? t('newQuote.customerCard.converting') : t('newQuote.customerCard.lead')}
              </span>
              <span className="flex flex-col">
                <span>{l.name}</span>
                <span className="text-xs font-normal text-muted">{[l.phone, l.email].filter(Boolean).join(' · ') || t('newQuote.customerCard.noContactInfo')}</span>
              </span>
            </button>
          ))}
          {customers && customers.items.length === 0 && leads.length === 0 ? <p className="text-sm text-muted">{t('newQuote.customerCard.noMatches')}</p> : null}
        </div>
        )}
      </CardContent>
      {workspaceId ? <AddCustomerDialog open={addOpen} onOpenChange={setAddOpen} workspaceId={workspaceId} onCreated={onSelectCustomer} /> : null}
    </Card>
  );
}

function AddCustomerDialog({
  open, onOpenChange, workspaceId, onCreated,
}: {
  open: boolean; onOpenChange: (v: boolean) => void; workspaceId: string; onCreated: (id: string) => void;
}) {
  const { t } = useTranslation('quotes');
  const createCustomer = useCreateCustomer(workspaceId);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateCustomerInput>({ resolver: zodResolver(createCustomerSchema) });

  async function onSubmit(values: CreateCustomerInput) {
    const created = await createCustomer.mutateAsync(values);
    toast.success(t('newQuote.toast.customerAdded'));
    reset();
    onOpenChange(false);
    onCreated(created.id);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={t('newQuote.addCustomerDialog.title')}>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="fullName">{t('newQuote.addCustomerDialog.fullName')}</Label>
          <Input id="fullName" {...register('fullName')} />
          <FieldError>{errors.fullName?.message}</FieldError>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="phone">{t('newQuote.addCustomerDialog.phone')}</Label>
          <Input id="phone" {...register('phone')} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="email">{t('newQuote.addCustomerDialog.email')}</Label>
          <Input id="email" type="email" {...register('email', { setValueAs: (v) => v || undefined })} />
          <FieldError>{errors.email?.message}</FieldError>
        </div>
        <Button type="submit" loading={createCustomer.isPending} className="mt-1 w-full">
          {t('newQuote.addCustomerDialog.submit')}
        </Button>
      </form>
    </Dialog>
  );
}

// Fullscreen photo viewer matching the customer quote portal's media lightbox
// (public-quote/PublicQuotePage.tsx): black backdrop, click anywhere to close.
function PhotoLightbox({ url, onClose }: { url: string; onClose: () => void }) {
  const { t } = useTranslation('quotes');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/95" onClick={onClose}>
      <button
        onClick={onClose}
        aria-label={t('newQuote.photoLightbox.close')}
        className="absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-full bg-black/40 text-white"
      >
        <X size={20} />
      </button>
      <img src={url} alt="" onClick={(e) => e.stopPropagation()} className="max-h-full max-w-full object-contain" />
    </div>
  );
}

function vehicleLabel(v: { year: number | null; make: string; model: string; notes: string }, t: TFn): string {
  return [v.year, v.make, v.model].filter(Boolean).join(' ') || v.notes || t('newQuote.unnamedVehicle');
}

const VEHICLE_PAGE_SIZE = 6;

function VehicleAndPhotosCard({
  workspaceId, customerId, vehicleId, onSelectVehicle, selectedPhotoId, onSelectPhoto,
}: {
  workspaceId: string | null; customerId: string | null; vehicleId: string | null; onSelectVehicle: (id: string) => void;
  selectedPhotoId: string | null; onSelectPhoto: (id: string) => void;
}) {
  const { t } = useTranslation('quotes');
  const queryClient = useQueryClient();
  const { data: vehicles = [] } = useCustomerVehicles(workspaceId, customerId ?? undefined);
  const { data: vehicle } = useVehicle(workspaceId, vehicleId ?? undefined);
  const uploadMedia = useUploadMedia(workspaceId);
  const [addOpen, setAddOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [enlargedUrl, setEnlargedUrl] = useState<string | null>(null);
  const debouncedSearch = useDebounce(search, 300);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, customerId]);

  const filteredVehicles = vehicles.filter((v) => vehicleLabel(v, t).toLowerCase().includes(debouncedSearch.trim().toLowerCase()));
  const totalPages = Math.max(1, Math.ceil(filteredVehicles.length / VEHICLE_PAGE_SIZE));
  const pageVehicles = filteredVehicles.slice((page - 1) * VEHICLE_PAGE_SIZE, page * VEHICLE_PAGE_SIZE);

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !vehicleId) return;
    await uploadMedia.mutateAsync({ file, kind: 'vehicle_photo', vehicleId });
    e.target.value = '';
    await queryClient.invalidateQueries({ queryKey: [workspaceId, 'vehicles', vehicleId] });
  }

  if (!customerId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('newQuote.vehicleCard.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted">{t('newQuote.vehicleCard.selectCustomerFirst')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('newQuote.vehicleCard.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <Input
            placeholder={t('newQuote.vehicleCard.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={addOpen}
            className="flex-1"
          />
          <Button type="button" size="sm" variant={addOpen ? 'secondary' : 'outline'} className="gap-1.5" onClick={() => setAddOpen((v) => !v)}>
            <Plus size={14} /> {t('newQuote.vehicleCard.addVehicle')}
          </Button>
        </div>

        {addOpen ? (
          workspaceId ? (
            <InlineAddVehicleForm
              workspaceId={workspaceId}
              customerId={customerId}
              onCreated={(id) => {
                onSelectVehicle(id);
                setAddOpen(false);
              }}
            />
          ) : null
        ) : (
          <>
            <div className="flex flex-wrap gap-2">
              {pageVehicles.map((v) => (
                <button
                  key={v.id}
                  onClick={() => onSelectVehicle(v.id)}
                  className={`border px-3 py-2 text-sm ${vehicleId === v.id ? 'border-accent bg-accent/10 font-semibold' : 'border-border hover:bg-surface-2'}`}
                >
                  {vehicleLabel(v, t)}
                </button>
              ))}
              {vehicles.length === 0 ? <p className="text-sm text-muted">{t('newQuote.vehicleCard.noVehicles')}</p> : null}
              {vehicles.length > 0 && filteredVehicles.length === 0 ? <p className="text-sm text-muted">{t('newQuote.vehicleCard.noMatches')}</p> : null}
            </div>

            {filteredVehicles.length > VEHICLE_PAGE_SIZE ? (
              <div className="flex items-center justify-between text-xs text-muted">
                <span>{t('newQuote.vehicleCard.pageOf', { page, totalPages })}</span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                    {t('newQuote.vehicleCard.prevPage')}
                  </Button>
                  <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                    {t('newQuote.vehicleCard.nextPage')}
                  </Button>
                </div>
              </div>
            ) : null}
          </>
        )}

        {vehicleId ? (
          <div>
            <div className="mb-2 text-[10px] font-semibold uppercase tracking-[.1em] text-muted">
              {t('newQuote.vehicleCard.customerPhotos')} <span className="text-accent-ink">{t('newQuote.vehicleCard.tapToSelect')}</span>
            </div>
            <div className="flex flex-wrap gap-2.5">
              {(vehicle?.photos ?? []).map((p, i) => {
                const isSelected = selectedPhotoId ? selectedPhotoId === p.id : i === 0;
                return (
                  <div key={p.id} className={`relative h-[100px] w-[150px] overflow-hidden border-2 ${isSelected ? 'border-accent' : 'border-transparent'}`}>
                    <button onClick={() => onSelectPhoto(p.id)} className="h-full w-full">
                      <img src={p.thumbUrl ?? p.url} alt="" className="h-full w-full object-cover" />
                    </button>
                    <button
                      onClick={() => setEnlargedUrl(p.url)}
                      aria-label={t('newQuote.photoLightbox.enlarge')}
                      className="absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-full bg-black/40 text-white"
                    >
                      <ZoomIn size={14} />
                    </button>
                  </div>
                );
              })}
              <label className="grid h-[100px] w-[150px] cursor-pointer place-items-center border-1.5 border-dashed border-border text-xs font-semibold text-muted hover:border-ink hover:text-ink">
                {t('newQuote.vehicleCard.addPhoto')}
                <input type="file" accept="image/*" className="hidden" onChange={handleUpload} />
              </label>
            </div>
          </div>
        ) : null}
      </CardContent>

      {enlargedUrl ? <PhotoLightbox url={enlargedUrl} onClose={() => setEnlargedUrl(null)} /> : null}
    </Card>
  );
}

// Mirrors pricing/VehiclePicker.tsx's CustomVehicleForm layout (search bar +
// toggle button revealing an inline creation form) so adding a vehicle here
// in step 2 feels the same as the "vehicle not listed?" flow in step 3's
// vehicle-aware builders, instead of a separate modal dialog.
function InlineAddVehicleForm({
  workspaceId, customerId, onCreated,
}: {
  workspaceId: string; customerId: string; onCreated: (id: string) => void;
}) {
  const { t } = useTranslation('quotes');
  const yearId = useId();
  const makeId = useId();
  const modelId = useId();
  const colorId = useId();
  const createVehicle = useCreateVehicle(workspaceId, customerId);
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateVehicleInput>({ resolver: zodResolver(createVehicleSchema) });
  const { makeOptions, modelOptions } = useVehicleCatalogSuggestions(workspaceId, watch('make') ?? '', watch('model') ?? '');

  async function onSubmit(values: CreateVehicleInput) {
    const created = await createVehicle.mutateAsync(values);
    toast.success(t('newQuote.toast.vehicleAdded'));
    reset();
    onCreated(created.id);
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3 border border-dashed border-border p-3" noValidate>
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={yearId}>{t('newQuote.addVehicleDialog.year')}</Label>
          <Input id={yearId} type="number" {...register('year', { valueAsNumber: true })} />
          <FieldError>{errors.year?.message}</FieldError>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={makeId}>{t('newQuote.addVehicleDialog.make')}</Label>
          <AutocompleteInput id={makeId} value={watch('make') ?? ''} onChange={(v) => setValue('make', v)} options={makeOptions} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={modelId}>{t('newQuote.addVehicleDialog.model')}</Label>
          <AutocompleteInput id={modelId} value={watch('model') ?? ''} onChange={(v) => setValue('model', v)} options={modelOptions} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={colorId}>{t('newQuote.addVehicleDialog.color')}</Label>
          <Input id={colorId} {...register('color')} />
        </div>
      </div>
      <Button type="submit" loading={createVehicle.isPending} className="mt-1 w-full">
        {t('newQuote.addVehicleDialog.submit')}
      </Button>
    </form>
  );
}

// One-line summary + subtitle for an already-added line, covering all 4
// engine-priced service kinds plus a defensive fallback for the hydration
// edge case where a very old pre-engine quote line has no serviceKind.
function lineSummary(line: DraftLine, _catalog: CatalogResponse | undefined, t: TFn): { title: string; subtitle: string } {
  if (getAddonLineMarker(line.breakdown)) {
    return { title: line.addonName ?? t('newQuote.lineSummary.addonFallback'), subtitle: t('newQuote.lineSummary.addonBadge') };
  }
  switch (line.serviceKind) {
    case 'wrap':
      return {
        title: line.vehicleLabel ?? t('newQuote.lineSummary.wrapFallback'),
        subtitle: t('newQuote.lineSummary.finishSuffix', { finish: t(`pricing.material.finish.${line.finish}.label`) }),
      };
    case 'ppf': {
      if (line.coverage?.serviceKind !== 'ppf') return { title: line.vehicleLabel ?? t('newQuote.lineSummary.ppfFallback'), subtitle: '' };
      const { zoneKey, filmTier, sizeClass } = line.coverage;
      return {
        title: line.vehicleLabel ?? t('newQuote.lineSummary.ppfFallback'),
        subtitle: [t(PPF_ZONE_LABEL_KEYS[zoneKey]), filmTier ? t(PPF_FILM_TIER_LABEL_KEYS[filmTier]) : '', t(SIZE_CLASS_LABEL_KEYS[sizeClass])].filter(Boolean).join(' · '),
      };
    }
    case 'tint': {
      if (line.coverage?.serviceKind !== 'tint') return { title: line.vehicleLabel ?? t('newQuote.lineSummary.tintFallback'), subtitle: '' };
      const { zoneKey, filmGrade, vlt, sizeClass } = line.coverage;
      return {
        title: line.vehicleLabel ?? t('newQuote.lineSummary.tintFallback'),
        subtitle: [t(TINT_ZONE_LABEL_KEYS[zoneKey]), filmGrade ? t(TINT_FILM_GRADE_LABEL_KEYS[filmGrade]) : '', t('pricing.tint.vltOption', { vlt }), t(SIZE_CLASS_LABEL_KEYS[sizeClass])].filter(Boolean).join(' · '),
      };
    }
    case 'ceramic': {
      if (line.coverage?.serviceKind !== 'ceramic') return { title: line.vehicleLabel ?? t('newQuote.lineSummary.ceramicFallback'), subtitle: '' };
      const { packageKey, sizeClass } = line.coverage;
      return {
        title: line.vehicleLabel ?? t('newQuote.lineSummary.ceramicFallback'),
        subtitle: [packageKey ? t(CERAMIC_PACKAGE_LABEL_KEYS[packageKey]) : '', t(SIZE_CLASS_LABEL_KEYS[sizeClass])].filter(Boolean).join(' · '),
      };
    }
    case 'print': {
      if (line.coverage?.serviceKind !== 'print') return { title: line.vehicleLabel ?? t('newQuote.lineSummary.printFallback'), subtitle: '' };
      const { coverageLevel, designName } = line.coverage;
      return {
        title: line.vehicleLabel ?? t('newQuote.lineSummary.printFallback'),
        subtitle: [designName ?? '', t(PRINT_COVERAGE_LABEL_KEYS[coverageLevel])].filter(Boolean).join(' · '),
      };
    }
    default:
      return { title: t('newQuote.lineSummary.serviceFallback'), subtitle: '' };
  }
}

function AddedLineRow({
  line, catalog, isEditing, onEdit, onRemove, onUpdateLine,
}: {
  line: DraftLine; catalog: CatalogResponse | undefined; isEditing: boolean; onEdit: () => void; onRemove: () => void;
  onUpdateLine?: (updated: DraftLine) => void;
}) {
  const { t } = useTranslation('quotes');
  const { formatMoney } = useLocaleFormatters();
  const { title, subtitle } = lineSummary(line, catalog, t);
  const addonMarker = getAddonLineMarker(line.breakdown);

  function handleHoursChange(hoursStr: string) {
    if (!addonMarker || !onUpdateLine) return;
    const h = Math.max(0, Number(hoursStr) || 0);
    const minutes = Math.round(h * 60);
    const lineTotalCents = priceAddonLine({ pricingType: 'hourly', rateCents: addonMarker.rateCents, minutes }).cents;
    onUpdateLine({
      ...line,
      estimatedMinutes: minutes,
      lineTotalCents,
      breakdown: { ...line.breakdown, addon: { ...addonMarker, minutes } },
    });
  }

  return (
    <div className="flex items-center justify-between gap-3 border border-border p-3">
      <div className="flex min-w-0 flex-col">
        <span className="truncate text-sm font-semibold">{title}</span>
        <span className="truncate text-xs text-muted">{subtitle}</span>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {addonMarker && addonMarker.pricingType === 'hourly' ? (
          <div className="flex items-center gap-1">
            <Input
              type="number"
              min={0}
              step={0.25}
              value={(line.estimatedMinutes ?? 0) / 60}
              onChange={(e) => handleHoursChange(e.target.value)}
              aria-label={t('newQuote.serviceCard.addons.hoursLabel')}
              className="h-8 w-16 text-xs"
            />
            <span className="text-xs text-muted">{t('newQuote.serviceCard.addons.hoursLabel')}</span>
          </div>
        ) : null}
        {line.lineTotalCents !== undefined ? (
          <span className="font-mono text-xs font-semibold text-ink">{formatMoney(line.lineTotalCents)}</span>
        ) : null}
        {isEditing || addonMarker ? null : (
          <button onClick={onEdit} aria-label={t('newQuote.serviceCard.editLine')} className="grid h-8 w-8 place-items-center text-muted hover:bg-surface-2 hover:text-ink">
            <Pencil size={14} />
          </button>
        )}
        <button onClick={onRemove} aria-label={t('newQuote.serviceCard.removeLine')} className="grid h-8 w-8 place-items-center text-danger hover:bg-danger/10">
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

function inferServiceKind(text: string): 'wrap' | 'ppf' | 'tint' | 'ceramic' | 'print' | null {
  if (!text) return null;
  const lower = text.toLowerCase();
  if (/print|graphic|印刷/.test(lower)) return 'print';
  if (/\bppf\b|paint protection/.test(lower)) return 'ppf';
  if (/\btint\b|窗膜|车窗/.test(lower)) return 'tint';
  if (/ceramic|coating|镀膜/.test(lower)) return 'ceramic';
  if (/wrap|贴膜|改色/.test(lower)) return 'wrap';
  return null;
}

const SERVICE_KINDS: { kind: 'wrap' | 'ppf' | 'tint' | 'ceramic' | 'print'; labelKey: string }[] = [
  { kind: 'wrap', labelKey: 'newQuote.serviceKind.wrap' },
  { kind: 'ppf', labelKey: 'newQuote.serviceKind.ppf' },
  { kind: 'tint', labelKey: 'newQuote.serviceKind.tint' },
  { kind: 'ceramic', labelKey: 'newQuote.serviceKind.ceramic' },
  { kind: 'print', labelKey: 'newQuote.serviceKind.print' },
];

const SERVICE_KIND_LABELS: Record<'wrap' | 'ppf' | 'tint' | 'ceramic' | 'print', string> = {
  wrap: 'Vinyl Wrap',
  ppf: 'Paint Protection Film',
  tint: 'Window Tint',
  ceramic: 'Ceramic Coating',
  print: 'Printed Wrap',
};

// Unified "3 - Service Details": pick a service kind, then configure it via
// the matching vehicle-aware builder. All 4 kinds need a vehicle before
// anything else makes sense, so they all follow the same VehiclePicker-first
// flow (VehicleAwareWrapLineBuilder / PpfLineBuilder / TintLineBuilder /
// CeramicLineBuilder), replacing the old split between a dynamic
// legacy-catalog-driven picker and hand-typed color/finish/VLT fields.
function ServiceDetailsCard({
  workspaceId, customerId, vehicleId, catalog, lines, setLines, customerNotes, setCustomerNotes, defaultKind,
  draftServiceState, setDraftServiceState, addonServices,
}: {
  workspaceId: string | null;
  customerId: string | null;
  vehicleId: string | null;
  catalog: CatalogResponse | undefined;
  lines: DraftLine[];
  setLines: Dispatch<SetStateAction<DraftLine[]>>;
  customerNotes: string;
  setCustomerNotes: (v: string) => void;
  defaultKind?: 'wrap' | 'ppf' | 'tint' | 'ceramic' | 'print' | null;
  // In-progress builder state (kind + opaque per-builder state), lifted to the
  // page so it rides the autosave payload (pricing_snapshot.draftServiceState)
  // and is restored open on reopen. See DraftServiceState.
  draftServiceState: DraftServiceState | null;
  setDraftServiceState: Dispatch<SetStateAction<DraftServiceState | null>>;
  // Active §36 add-on services (Settings > Pricing) - fetched at the page
  // level (see NewQuotePage) since the page also needs them wired for the
  // autosave/estimate math, not just this card's picker UI.
  addonServices: AddonService[];
}) {
  const { t } = useTranslation('quotes');
  const { formatMoney } = useLocaleFormatters();
  // Same vehicle already selected in step 2 - reused here (react-query cache
  // hit, not an extra request) so the wrap builder's vehicle search can be
  // pre-filled instead of asking the shop to re-enter the make/model.
  const { data: customerVehicle } = useVehicle(workspaceId, vehicleId ?? undefined);
  const customerVehicleHint = customerVehicle ? { year: customerVehicle.year, make: customerVehicle.make, model: customerVehicle.model, id: customerVehicle.id, color: customerVehicle.color } : null;

  const [stagingKind, setStagingKind] = useState<'wrap' | 'ppf' | 'tint' | 'ceramic' | 'print'>(draftServiceState?.kind ?? 'wrap');
  const [editingKey, setEditingKey] = useState<string | null>(null);

  // Restore which kind's builder was open once the persisted draft state
  // hydrates (async), unless the shop is already editing or has changed it.
  const stagingRestoredRef = useRef(false);
  useEffect(() => {
    if (stagingRestoredRef.current || !draftServiceState || editingKey) return;
    stagingRestoredRef.current = true;
    setStagingKind(draftServiceState.kind);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftServiceState]);

  // The builder emits its full serialized state here; store it tagged with the
  // currently open kind so reopening restores exactly this builder. Cleared
  // (below) when the line is added, the kind changes, or an edit begins.
  const handleBuilderState = useCallback((state: Record<string, unknown>) => {
    setDraftServiceState({ kind: stagingKind, state });
  }, [setDraftServiceState, stagingKind]);

  // Lead-converted drafts hydrate async: once the inferred kind arrives,
  // preselect it - but never while the shop is editing, has added lines, or has
  // a restored in-progress builder.
  const kindDefaultedRef = useRef(false);
  useEffect(() => {
    if (kindDefaultedRef.current || !defaultKind) return;
    kindDefaultedRef.current = true;
    if (lines.length === 0 && !editingKey && !draftServiceState) setStagingKind(defaultKind);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultKind]);
  const editingLine = editingKey ? lines.find((l) => l.key === editingKey) ?? null : null;

  function removeLine(key: string) {
    setLines((prev) => prev.filter((l) => l.key !== key));
    if (editingKey === key) setEditingKey(null);
  }

  function startEdit(line: DraftLine) {
    // Abandon any in-progress builder state before switching to edit an existing line.
    setDraftServiceState(null);
    setEditingKey(line.key);
    setStagingKind((line.serviceKind ?? 'wrap') as typeof stagingKind);
  }

  function cancelEdit() {
    setEditingKey(null);
  }

  // On edit, replaces the line in place (keeping its key/position). On add,
  // appends and clears the in-progress builder state (it's now a committed line).
  function handleSubmitLine(built: DraftLine) {
    if (editingKey) {
      setLines((prev) => prev.map((l) => (l.key === editingKey ? { ...built, key: editingKey } : l)));
      setEditingKey(null);
      return;
    }
    // At most one line per service category (wrap/ppf/tint/ceramic) - a second
    // of the same kind is rejected rather than added. Add-on lines (no
    // serviceKind) are excluded from this check entirely - they're not a
    // builder-priced category.
    const kind = built.serviceKind ?? 'wrap';
    if (lines.some((l) => !getAddonLineMarker(l.breakdown) && (l.serviceKind ?? 'wrap') === kind)) {
      const label = SERVICE_KINDS.find((s) => s.kind === kind);
      toast.error(t('newQuote.serviceCard.duplicateKind', { service: label ? t(label.labelKey) : kind }));
      return;
    }
    setLines((prev) => [...prev, built]);
    setDraftServiceState(null);
  }

  // Each kind's legacy service_types row still supplies the placeholder
  // serviceTypeId/coverageId/materialId that quoteLineItemInputSchema's
  // legacy columns require - resolved here via classifyServiceKind since the
  // fixed 4-kind picker above no longer reads catalog.serviceTypes directly.
  const matchedService = catalog?.serviceTypes.find((s) => classifyServiceKind(s.name) === stagingKind);
  const placeholderIds = matchedService
    ? { serviceTypeId: matchedService.id, coverageId: matchedService.coverages[0]?.id ?? '', materialId: matchedService.materials[0]?.id ?? '' }
    : null;

  function guardedAdd<T>(builderOutput: T, build: (output: T, ids: { serviceTypeId: string; coverageId: string; materialId: string }, sort: number) => DraftLine) {
    if (!placeholderIds) {
      toast.error(t('newQuote.serviceCard.notConfigured'));
      return;
    }
    handleSubmitLine(build(builderOutput, placeholderIds, lines.length));
  }

  // Steps 1 and 2 (customer + vehicle) must be complete before this step is
  // editable. Hooks above are still called unconditionally either way.
  if (!customerId || !vehicleId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('newQuote.serviceCard.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted">{t('newQuote.serviceCard.completeStepsFirst')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('newQuote.serviceCard.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {lines.length > 0 ? (
          <div className="flex flex-col gap-1.5">
            {lines.map((line) => (
              <AddedLineRow
                key={line.key}
                line={line}
                catalog={catalog}
                isEditing={line.key === editingKey}
                onEdit={() => startEdit(line)}
                onRemove={() => removeLine(line.key)}
                onUpdateLine={(updated) => setLines((prev) => prev.map((l) => (l.key === updated.key ? updated : l)))}
              />
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted">{t('newQuote.serviceCard.noServices')}</p>
        )}

        <div className="flex flex-col gap-3 border border-dashed border-border p-3">
          <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[.08em] text-muted">
            {editingLine ? (
              <>
                <Pencil size={14} /> {t('newQuote.serviceCard.editingService')}
                <button type="button" onClick={cancelEdit} className="ml-auto text-[11px] font-semibold normal-case tracking-normal text-accent-ink hover:underline">
                  {t('pricing.cancelEdit')}
                </button>
              </>
            ) : (
              <>
                <Plus size={14} /> {t('newQuote.serviceCard.addService')}
              </>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="staging-service">{t('newQuote.serviceCard.serviceType')}</Label>
            <select
              id="staging-service"
              value={stagingKind}
              disabled={!!editingLine}
              onChange={(e) => {
                // Switching kind remounts the builder; drop the old kind's in-progress state.
                setDraftServiceState(null);
                setStagingKind(e.target.value as typeof stagingKind);
              }}
              className="h-9 border border-border bg-surface px-2 text-sm disabled:opacity-60"
            >
              {SERVICE_KINDS.map((s) => (
                <option key={s.kind} value={s.kind}>{t(s.labelKey)}</option>
              ))}
            </select>
          </div>

          {stagingKind === 'wrap' && matchedService ? (
            <VehicleAwareWrapLineBuilder
              key={`${stagingKind}-${editingKey ?? 'add'}-${vehicleId ?? 'none'}`}
              workspaceId={workspaceId}
              customerId={customerId}
              service={matchedService}
              customerVehicleHint={customerVehicleHint}
              editingLine={editingLine}
              onCancelEdit={cancelEdit}
              onAddLine={(output) => handleSubmitLine(buildDraftLineFromWrapOutput(output, matchedService, lines.length))}
              initialState={draftServiceState?.kind === 'wrap' ? draftServiceState.state : undefined}
              onStateChange={handleBuilderState}
            />
          ) : stagingKind === 'ppf' ? (
            <PpfLineBuilder
              key={`${stagingKind}-${editingKey ?? 'add'}-${vehicleId ?? 'none'}`}
              workspaceId={workspaceId}
              customerId={customerId}
              customerVehicleHint={customerVehicleHint}
              editingLine={editingLine}
              onCancelEdit={cancelEdit}
              onAddLine={(output) => guardedAdd(output, buildDraftLineFromPpfOutput)}
              initialState={draftServiceState?.kind === 'ppf' ? draftServiceState.state : undefined}
              onStateChange={handleBuilderState}
            />
          ) : stagingKind === 'tint' ? (
            <TintLineBuilder
              key={`${stagingKind}-${editingKey ?? 'add'}-${vehicleId ?? 'none'}`}
              workspaceId={workspaceId}
              customerId={customerId}
              customerVehicleHint={customerVehicleHint}
              editingLine={editingLine}
              onCancelEdit={cancelEdit}
              onAddLine={(output) => guardedAdd(output, buildDraftLineFromTintOutput)}
              initialState={draftServiceState?.kind === 'tint' ? draftServiceState.state : undefined}
              onStateChange={handleBuilderState}
            />
          ) : stagingKind === 'ceramic' ? (
            <CeramicLineBuilder
              key={`${stagingKind}-${editingKey ?? 'add'}-${vehicleId ?? 'none'}`}
              workspaceId={workspaceId}
              customerId={customerId}
              customerVehicleHint={customerVehicleHint}
              editingLine={editingLine}
              onCancelEdit={cancelEdit}
              onAddLine={(output) => guardedAdd(output, buildDraftLineFromCeramicOutput)}
              initialState={draftServiceState?.kind === 'ceramic' ? draftServiceState.state : undefined}
              onStateChange={handleBuilderState}
            />
          ) : stagingKind === 'print' ? (
            <PrintLineBuilder
              key={`${stagingKind}-${editingKey ?? 'add'}-${vehicleId ?? 'none'}`}
              workspaceId={workspaceId}
              customerId={customerId}
              customerVehicleHint={customerVehicleHint}
              editingLine={editingLine}
              onCancelEdit={cancelEdit}
              onAddLine={(output) => guardedAdd(output, buildDraftLineFromPrintOutput)}
              initialState={draftServiceState?.kind === 'print' ? draftServiceState.state : undefined}
              onStateChange={handleBuilderState}
            />
          ) : stagingKind === 'wrap' ? (
            <p className="text-sm text-muted">{t('newQuote.serviceCard.wrapNotConfigured')}</p>
          ) : null}
        </div>

        {addonServices.length > 0 ? (
          <div className="flex flex-col gap-2 border border-border p-3">
            <div className="text-xs font-semibold uppercase tracking-[.08em] text-muted">{t('newQuote.serviceCard.addons.title')}</div>
            <div className="flex flex-col gap-1.5">
              {addonServices.map((a) => {
                const added = lines.some((l) => getAddonLineMarker(l.breakdown)?.addonServiceId === a.id);
                return (
                  <div key={a.id} className="flex items-center justify-between gap-3 py-1">
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate text-sm font-semibold">{a.name}</span>
                      <span className="text-xs text-muted">
                        {a.pricingType === 'flat' ? formatMoney(a.rateCents) : t('newQuote.serviceCard.addons.perHour', { price: formatMoney(a.rateCents) })}
                      </span>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={added}
                      onClick={() => {
                        const minutes = a.estimatedMinutes ?? 0;
                        setLines((prev) => [
                          ...prev,
                          {
                            key: crypto.randomUUID(),
                            serviceTypeId: '',
                            coverageId: '',
                            materialId: '',
                            color: '',
                            finish: '',
                            notes: '',
                            sort: prev.length,
                            estimatedMinutes: minutes,
                            lineTotalCents: priceAddonLine({ pricingType: a.pricingType, rateCents: a.rateCents, minutes }).cents,
                            breakdown: { addon: { addonServiceId: a.id, pricingType: a.pricingType, rateCents: a.rateCents, minutes } },
                            addonName: a.name,
                          },
                        ]);
                      }}
                    >
                      {added ? t('newQuote.serviceCard.addons.added') : t('newQuote.serviceCard.addons.add')}
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="serviceNotes">{t('newQuote.serviceCard.additionalNotes')}</Label>
          <textarea
            id="serviceNotes"
            rows={3}
            value={customerNotes}
            onChange={(e) => setCustomerNotes(e.target.value)}
            placeholder={t('newQuote.serviceCard.notesPlaceholder')}
            className="border border-border bg-surface px-3 py-2 text-sm outline-none focus-visible:border-accent"
          />
        </div>
      </CardContent>
    </Card>
  );
}

function EstimateCard({
  workspaceId,
  lines, catalog, subtotalCents, taxRateBps, taxEnabled, setTaxEnabled, taxCents,
  discountName, setDiscountName, discountPct, setDiscountPct, discountCents,
  totalCents, depositPct, setDepositPct, depositCents,
  depositCustom, setDepositCustom, depositCustomCents, setDepositCustomCents,
  stripeLinked, categoryAdjustments, defaultShortfallCents,
}: {
  workspaceId: string | null;
  lines: DraftLine[]; catalog: CatalogResponse | undefined;
  subtotalCents: number; taxRateBps: number; taxEnabled: boolean; setTaxEnabled: (v: boolean) => void; taxCents: number;
  discountName: string; setDiscountName: (v: string) => void; discountPct: number; setDiscountPct: (v: number) => void; discountCents: number;
  totalCents: number; depositPct: number; setDepositPct: (v: number) => void; depositCents: number;
  depositCustom: boolean; setDepositCustom: (v: boolean) => void; depositCustomCents: number; setDepositCustomCents: (v: number) => void;
  stripeLinked: boolean;
  // Display-only local mirror of the server's §36 minimum-adjustment math (see
  // NewQuotePage's categoryAdjustments/defaultShortfallCents computation) -
  // never sent in the autosave payload; the server rebuilds these itself.
  categoryAdjustments: { category: 'wrap' | 'ppf' | 'tint' | 'ceramic' | 'print'; adjustmentCents: number }[];
  defaultShortfallCents: number;
}) {
  const { t } = useTranslation('quotes');
  const { formatMoney } = useLocaleFormatters();
  const [discountOpen, setDiscountOpen] = useState(false);
  const discountActive = discountOpen || discountPct > 0 || discountName.length > 0;
  const updatePricingRules = useUpdatePricingRules(workspaceId);
  const [newTaxRatePct, setNewTaxRatePct] = useState(0);

  async function handleSaveTaxRate() {
    await updatePricingRules.mutateAsync({ taxRateBps: Math.round(newTaxRatePct * 100) });
    toast.success(t('newQuote.estimateCard.taxRateSaved'));
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('newQuote.estimateCard.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        {lines.length > 0 ? (
          lines.map((line) => {
            const { title, subtitle } = lineSummary(line, catalog, t);
            return (
              <div key={line.key} className="flex items-center justify-between gap-2 text-[12.5px] text-muted">
                <span className="truncate">{title}{subtitle ? ` · ${subtitle}` : ''}</span>
                <span className="font-mono text-xs font-semibold text-ink">{formatMoney(line.lineTotalCents ?? 0)}</span>
              </div>
            );
          })
        ) : (
          <p className="text-sm text-muted">{t('newQuote.estimateCard.empty')}</p>
        )}

        {categoryAdjustments.map((adj) => (
          <div key={adj.category} className="flex items-center justify-between gap-2 text-[12.5px] text-muted">
            <span className="truncate">{t('newQuote.estimateCard.categoryMinimum', { service: t(`newQuote.serviceKind.${adj.category}`) })}</span>
            <span className="font-mono text-xs font-semibold text-ink">{formatMoney(adj.adjustmentCents)}</span>
          </div>
        ))}
        {defaultShortfallCents > 0 ? (
          <div className="flex items-center justify-between gap-2 text-[12.5px] text-muted">
            <span className="truncate">{t('newQuote.estimateCard.minimumJobPrice')}</span>
            <span className="font-mono text-xs font-semibold text-ink">{formatMoney(defaultShortfallCents)}</span>
          </div>
        ) : null}

        <div className="flex items-center justify-between gap-2 border-t border-border pt-2.5 text-[12.5px] text-muted">
          <span>{t('newQuote.estimateCard.subtotal')}</span>
          <span className="font-mono text-xs font-semibold text-ink">{formatMoney(subtotalCents)}</span>
        </div>

        {discountActive ? (
          <div className="flex flex-col gap-2 bg-surface-2 px-3 py-2.5">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">{t('newQuote.estimateCard.discountLabel')}</span>
              <button
                type="button"
                onClick={() => {
                  setDiscountName('');
                  setDiscountPct(0);
                  setDiscountOpen(false);
                }}
                aria-label={t('newQuote.estimateCard.removeDiscount')}
                className="grid h-6 w-6 shrink-0 place-items-center text-muted hover:text-danger"
              >
                ×
              </button>
            </div>
            <div className="grid grid-cols-[1fr_auto] gap-2">
              <div>
                <Label htmlFor="discount-name">{t('newQuote.estimateCard.discountName')}</Label>
                <Input
                  id="discount-name"
                  value={discountName}
                  onChange={(e) => setDiscountName(e.target.value)}
                  placeholder={t('newQuote.estimateCard.discountNamePlaceholder')}
                  maxLength={60}
                  className="mt-1 h-8 text-xs"
                />
              </div>
              <div>
                <Label htmlFor="discount-pct">{t('newQuote.estimateCard.discountPctLabel')}</Label>
                <div className="mt-1 flex items-center gap-1">
                  <Input
                    id="discount-pct"
                    type="number"
                    min={0}
                    max={100}
                    step={1}
                    value={discountPct}
                    onChange={(e) => setDiscountPct(Math.min(100, Math.max(0, Number(e.target.value))))}
                    className="h-8 w-16 text-xs"
                  />
                  <span className="text-xs text-muted">%</span>
                </div>
              </div>
            </div>
            <div className="text-right text-xs text-muted">
              <span className="font-mono">−{formatMoney(discountCents)}</span>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setDiscountOpen(true)}
            className="self-start text-xs font-semibold text-accent-ink hover:underline"
          >
            {t('newQuote.estimateCard.addDiscount')}
          </button>
        )}

        {taxRateBps > 0 ? (
          <div className="flex items-center justify-between gap-2 text-[12.5px] text-muted">
            <div className="flex items-center gap-1.5">
              <label className="flex items-center gap-1.5">
                <input type="checkbox" checked={taxEnabled} onChange={(e) => setTaxEnabled(e.target.checked)} />
                {t('newQuote.estimateCard.taxLabel')}
              </label>
              <Input
                type="number"
                min={0}
                max={30}
                step={0.01}
                defaultValue={(taxRateBps / 100).toFixed(2)}
                key={taxRateBps}
                onBlur={(e) => {
                  const bps = Math.round(Math.min(30, Math.max(0, Number(e.target.value))) * 100);
                  if (bps !== taxRateBps) {
                    updatePricingRules.mutate({ taxRateBps: bps }, { onSuccess: () => toast.success(t('newQuote.estimateCard.taxRateSaved')) });
                  }
                }}
                className="h-7 w-16 px-2 text-xs"
              />
              <span className="text-xs">%</span>
            </div>
            <span className="font-mono text-xs font-semibold text-ink">{formatMoney(taxCents)}</span>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-2 text-[11px] text-muted">
            <span>{t('newQuote.estimateCard.noTaxRate')}</span>
            <div className="flex items-center gap-1.5">
              <div className="flex items-center gap-1">
                <Input
                  type="number"
                  min={0}
                  max={30}
                  step={0.01}
                  value={newTaxRatePct}
                  onChange={(e) => setNewTaxRatePct(Math.min(30, Math.max(0, Number(e.target.value))))}
                  className="h-7 w-16 px-2 text-xs"
                />
                <span className="text-xs">%</span>
              </div>
              <Button size="sm" disabled={newTaxRatePct <= 0} loading={updatePricingRules.isPending} onClick={handleSaveTaxRate}>
                {t('newQuote.estimateCard.save')}
              </Button>
            </div>
          </div>
        )}

        <div className="flex items-baseline justify-between border-t border-border pt-2.5">
          <span className="text-[13px] font-bold">{t('newQuote.estimateCard.total')}</span>
          <span className="font-mono text-2xl font-semibold">{formatMoney(totalCents)}</span>
        </div>
        <div className="mt-1 flex flex-col gap-2 bg-surface-2 px-3 py-2.25">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">{t('newQuote.estimateCard.deposit')}</span>
              <select
                value={depositCustom ? 'custom' : depositPct}
                onChange={(e) => {
                  if (e.target.value === 'custom') {
                    setDepositCustom(true);
                    return;
                  }
                  setDepositCustom(false);
                  setDepositPct(Number(e.target.value));
                }}
                disabled={!stripeLinked}
                title={!stripeLinked ? t('newQuote.estimateCard.depositRequiresStripe') : undefined}
                className="border border-border bg-surface px-2 py-1 text-sm disabled:cursor-not-allowed disabled:opacity-60"
              >
                <option value={0}>{t('newQuote.estimateCard.noDeposit')}</option>
                {stripeLinked && <option value={10}>10%</option>}
                {stripeLinked && <option value={25}>25%</option>}
                {stripeLinked && <option value={50}>50%</option>}
                {stripeLinked && <option value="custom">{t('newQuote.estimateCard.customDeposit')}</option>}
              </select>
            </div>
            <span className="font-mono font-semibold">{formatMoney(depositCents)}</span>
          </div>
          {depositCustom ? (
            <div className="flex items-center gap-1.5 text-sm">
              <span className="text-muted">$</span>
              <Input
                type="number"
                min={0}
                value={depositCustomCents / 100}
                onChange={(e) => setDepositCustomCents(Math.max(0, Math.round(Number(e.target.value) * 100)))}
                className="h-8 w-28 text-xs"
              />
            </div>
          ) : null}
        </div>
        {!stripeLinked && (
          <p className="text-[11px] text-muted">{t('newQuote.estimateCard.depositRequiresStripe')}</p>
        )}
      </CardContent>
    </Card>
  );
}

function MockupPanel({
  workspaceId, quoteId, vehicleId, detailsComplete, selectedPhotoId, onSelectPhoto, onFlushSave, printDesignChosen,
}: {
  workspaceId: string | null; quoteId: string | null; vehicleId: string | null; detailsComplete: boolean;
  selectedPhotoId: string | null; onSelectPhoto: (id: string) => void; onFlushSave: () => Promise<void>;
  printDesignChosen: boolean;
}) {
  const { t } = useTranslation('quotes');
  const { membership } = useActiveMembership();
  const hdAllowed = membership ? PLAN_DEFINITIONS[membership.plan].gates.hdRenders : false;
  const { data: vehicle } = useVehicle(workspaceId, vehicleId ?? undefined);
  const { data: mockups = [] } = useMockups(workspaceId, quoteId ?? undefined);
  const { data: history = [] } = useVehicleMockupHistory(workspaceId, vehicleId ?? undefined);
  const generateMockup = useGenerateMockup(workspaceId, quoteId ?? '');
  const setMockupAttached = useSetMockupAttached(workspaceId, quoteId ?? '');
  const attachExisting = useAttachExistingMockup(workspaceId, quoteId ?? undefined);
  const [quality, setQuality] = useState<'standard' | 'hd'>('standard');
  const [enlargedUrl, setEnlargedUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!hdAllowed && quality === 'hd') setQuality('standard');
  }, [hdAllowed, quality]);

  const photos = vehicle?.photos ?? [];
  const effectiveSourceId = selectedPhotoId ?? photos[0]?.id ?? null;
  const reusableHistory = history.filter((m) => m.quoteId !== quoteId);

  async function handleGenerate(overrideQuality?: 'standard' | 'hd') {
    if (!quoteId || !detailsComplete) return;
    // Ensure any debounced draft edits (new/changed line items) are persisted
    // before generating - the server reads quote_line_items and rejects a quote
    // with none yet saved.
    await onFlushSave();
    const result = await generateMockup.mutateAsync({ sourceMediaId: effectiveSourceId ?? undefined, quality: overrideQuality ?? quality });
    if (result.mockup.status === 'pending') {
      toast.info(t('newQuote.mockupPanel.toastGenerating'));
    }
  }

  const failureReason = useCallback(
    (errorCode: string | null) =>
      t(`newQuote.mockupPanel.failureReason.${errorCode ?? 'unknown'}`, { defaultValue: t('newQuote.mockupPanel.failureReason.unknown') }),
    [t],
  );

  // Renders run detached server-side, so a failure arrives on a poll rather
  // than from the generate mutation - the user has usually looked away by
  // then. Toast the pending -> failed transition once per mockup.
  const prevStatusRef = useRef<Map<string, string>>(new Map());
  useEffect(() => {
    const prevStatus = prevStatusRef.current;
    for (const m of mockups) {
      const prev = prevStatus.get(m.id);
      if (prev === 'pending' && m.status === 'failed') {
        toast.error(t('newQuote.mockupPanel.toastFailed'), { description: failureReason(m.errorCode) });
      }
      prevStatus.set(m.id, m.status);
    }
  }, [mockups, t, failureReason]);

  async function handleReuse(mockupId: string) {
    if (!quoteId) return;
    await attachExisting.mutateAsync(mockupId);
    toast.success(t('newQuote.mockupPanel.toastAdded'));
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('newQuote.mockupPanel.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {printDesignChosen ? (
          <p className="text-sm text-muted">{t('newQuote.mockupPanel.printDesignChosen')}</p>
        ) : !quoteId ? (
          <p className="text-sm text-muted">{t('newQuote.mockupPanel.unlockHint')}</p>
        ) : (
          <>
            {!detailsComplete ? (
              <p className="text-sm text-muted">{t('newQuote.mockupPanel.fillDetailsHint')}</p>
            ) : null}

            {photos.length === 0 ? (
              <p className="text-sm text-muted">{t('newQuote.mockupPanel.noPhotos')}</p>
            ) : (
              <div className="flex gap-2 overflow-x-auto">
                {photos.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => onSelectPhoto(p.id)}
                    className={`h-16 w-16 shrink-0 overflow-hidden border-2 ${effectiveSourceId === p.id ? 'border-accent' : 'border-transparent'}`}
                  >
                    <img src={p.thumbUrl ?? p.url} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <select
                value={quality}
                onChange={(e) => setQuality(e.target.value as 'standard' | 'hd')}
                className="h-9 min-w-0 border border-border bg-surface px-2 text-sm"
              >
                <option value="standard">{t('newQuote.mockupPanel.qualityStandard')}</option>
                <option value="hd" disabled={!hdAllowed} title={!hdAllowed ? t('newQuote.mockupPanel.qualityHdLocked') : undefined}>
                  {t('newQuote.mockupPanel.qualityHd')}
                </option>
              </select>
              <Button size="sm" loading={generateMockup.isPending} disabled={!detailsComplete} onClick={() => handleGenerate()}>
                {t('newQuote.mockupPanel.generate')}
              </Button>
            </div>
            {!hdAllowed && (
              <p className="text-[11px] text-muted">{t('newQuote.mockupPanel.qualityHdLocked')}</p>
            )}

            {mockups.length > 0 ? (
              <div className="flex flex-col gap-2">
                <p className="text-xs text-muted">{t('newQuote.mockupPanel.tapHint')}</p>
                {mockups.map((m) => (
                  <div
                    key={m.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => m.status === 'succeeded' && setMockupAttached.mutate({ mockupId: m.id, attached: !m.attached })}
                    onKeyDown={(e) => {
                      if (m.status === 'succeeded' && (e.key === 'Enter' || e.key === ' ')) setMockupAttached.mutate({ mockupId: m.id, attached: !m.attached });
                    }}
                    className={`flex cursor-pointer items-center gap-2 border-2 p-2 ${m.attached ? 'border-accent' : 'border-transparent'}`}
                  >
                    {m.url ? (
                      <img
                        src={m.url}
                        alt=""
                        onClick={(e) => {
                          e.stopPropagation();
                          setEnlargedUrl(m.url);
                        }}
                        className="h-14 w-14 object-cover"
                      />
                    ) : (
                      <div className={`grid h-14 w-14 place-items-center bg-surface-2 text-[10px] text-muted ${m.status === 'pending' ? 'animate-pulse' : ''}`}>
                        {t(`newQuote.mockupPanel.statusLabel.${m.status}`, { defaultValue: m.status })}
                      </div>
                    )}
                    <div className="flex-1 text-xs">
                      <div className="font-semibold">
                        {t(`newQuote.mockupPanel.quality.${m.quality}`, { defaultValue: m.quality })} · {t(`newQuote.mockupPanel.statusLabel.${m.status}`, { defaultValue: m.status })}
                      </div>
                      <div className="text-muted">
                        {t('newQuote.mockupPanel.credits', { count: m.creditsSpent })}
                        {m.cacheHit ? t('newQuote.mockupPanel.cached') : ''}
                      </div>
                      {m.status === 'succeeded' ? (
                        <div className="font-semibold text-accent-ink">{m.attached ? t('newQuote.mockupPanel.selected') : t('newQuote.mockupPanel.notAttached')}</div>
                      ) : null}
                      {m.status === 'failed' ? (
                        <div className="text-danger">{failureReason(m.errorCode)}</div>
                      ) : null}
                    </div>
                    {m.status === 'failed' ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        className="shrink-0"
                        loading={generateMockup.isPending}
                        disabled={!detailsComplete}
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleGenerate(m.quality);
                        }}
                      >
                        {t('newQuote.mockupPanel.retry')}
                      </Button>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : null}

            {reusableHistory.length > 0 ? (
              <div className="flex flex-col gap-2 border-t border-border pt-3">
                <p className="text-xs text-muted">{t('newQuote.mockupPanel.pastMockupsHint')}</p>
                <div className="flex flex-wrap gap-2.5">
                  {reusableHistory.map((m) => (
                    <div key={m.id} className="flex w-[92px] flex-col items-center gap-1 text-center">
                      <button
                        type="button"
                        onClick={() => m.url && setEnlargedUrl(m.url)}
                        className="h-[70px] w-[92px] overflow-hidden border border-border"
                      >
                        {m.url ? <img src={m.thumbUrl ?? m.url} alt="" className="h-full w-full object-cover" /> : null}
                      </button>
                      <span className="text-[10px] text-muted">Q-{String(m.quoteNumber).padStart(4, '0')}</span>
                      <Button
                        size="sm"
                        variant="secondary"
                        className="h-6 w-full px-1 text-[10px]"
                        loading={attachExisting.isPending}
                        disabled={!quoteId}
                        onClick={() => handleReuse(m.id)}
                      >
                        {t('newQuote.mockupPanel.addToQuote')}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </>
        )}
      </CardContent>

      {enlargedUrl ? <PhotoLightbox url={enlargedUrl} onClose={() => setEnlargedUrl(null)} /> : null}
    </Card>
  );
}

